import { Suspense, lazy, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { Box } from "@mui/material";
import useTransactionPolling from "@/hooks/useTransactionPolling";
import SpaceDashboardOutlinedIcon from "@mui/icons-material/SpaceDashboardOutlined";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import CalendarViewMonthOutlinedIcon from "@mui/icons-material/CalendarViewMonthOutlined";
import FaceIcon from "@mui/icons-material/Face";
import MapsHomeWorkOutlinedIcon from "@mui/icons-material/MapsHomeWorkOutlined";
import TapasOutlinedIcon from "@mui/icons-material/TapasOutlined";
import ImageOutlinedIcon from "@mui/icons-material/ImageOutlined";
import PlayCircleOutlineOutlinedIcon from "@mui/icons-material/PlayCircleOutlineOutlined";
import MicIcon from "@mui/icons-material/Mic";
import AudiotrackOutlinedIcon from "@mui/icons-material/AudiotrackOutlined";
import LocalMoviesOutlinedIcon from "@mui/icons-material/LocalMoviesOutlined";
import SettingsOutlinedIcon from "@mui/icons-material/SettingsOutlined";
import { toastError, toastInfo, toastSuccess } from "@/utils/toast";
import AppHeader from "@/components/AppHeader";
import LeftSidebar from "@/components/ProjectPage/LeftSidebar";
import RightSidebar from "@/components/ProjectPage/RightSidebar/RightSidebar";
import type { NavGroup } from "@/types/navigation";
import type { ProjectDetailsProject } from "@/types/project";
import LeftToolsPanel from "@/components/ProjectPage/LeftToolsPanel/LeftToolsPanel";
import Loading from "@/pages/Loading";

const PageContent = lazy(() => import("@/components/ProjectPage/PageContent"));
const ScriptMainSpace = lazy(() => import("@/components/ProjectPage/Mainspaces/ScriptMainSpace"));
const StyleDnaMainSpace = lazy(() => import("@/components/ProjectPage/Mainspaces/StyleDnaMainSpace"));
const SystemSettingsMainSpace = lazy(() => import("@/components/ProjectPage/Mainspaces/SystemSettingsMainSpace"));
import { useLockProject, useProjectDetails, useStyle, useUnlockProject, useUpdateProject } from "@/api/api";
import { useUpdateProjectMetaV2 } from "@/api/entityOpsV2";
import { useProjectCachePatch } from "@/hooks/useProjectCachePatch";
import { useProjectPermissions } from "@/hooks/useProjectPermissions";
import { getMetaVersion, patchProjectMetaMerged } from "@/services/projectCachePatch";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import LoadingDialog from "@/components/ui/LoadingDialog";
import { useStore } from "@/contexts/StoreContext";
import { observer } from "mobx-react-lite";
import RenameProjectDialog from "@/components/ui/RenameEntityDialog";
import { useQueryClient } from "@tanstack/react-query";
import {
  AppRoute,
  ProjectPanelRoute,
  activeIndexForProjectPanelEntity,
  entityIdExistsInProjectPanel,
  entitySlugForProjectPanel,
  parseLibraryMediaHighlightFromSearch,
  projectPanelPath,
  projectPanelUsesEntitySlug,
  searchWithoutLibraryMediaHighlight,
  type LibraryMediaHighlight
} from "@/types/routes";
import { assignedStyleDnaIdFromProject, styleDnaGenerationsHaveSelected } from "@/utils/styleDna";
import { approvedStoryboardShotVideoId } from "@/services/storyboard";
import { isProjectDetailsNewerThan } from "@/services/projectDetails";
import {
  projectPanelEntityGroup,
  readLastVisitedProjectEntityId,
  saveLastVisitedProject,
  saveLastVisitedProjectEntityId,
  saveLastVisitedProjectPanelPath
} from "@/services/projectLastVisitedPanel";
import {
  APP_HEADER_HEIGHT_PX,
  DEFAULT_TAB_REDIRECT_PATH,
  DEFAULT_TRANSACTION_INTERVAL_MS,
  LEFT_TOOLS_PANEL_WIDTH_PX,
  LEFT_TOOLS_STYLE_DNA_PANEL_WIDTH,
  NAV_RAIL_COLLAPSED_WIDTH_PX,
  NAV_RAIL_EXPANDED_WIDTH_PX,
  RIGHT_SIDEBAR_WIDTH_PX
} from "@/utils/constants";

const SCRIPT_PENDING_JOB_DESCRIPTION = "Generating script" as const;
const TEXT_EXTRACTION_PENDING_JOB_DESCRIPTION = "Extracting text" as const;
const BREAKDOWN_PENDING_JOB_DESCRIPTION = "Generating breakdown" as const;

function pendingJobLoadingLabel(description: string): string {
  if (description === SCRIPT_PENDING_JOB_DESCRIPTION) {
    return "Generating Screenplay…";
  }

  if (description === TEXT_EXTRACTION_PENDING_JOB_DESCRIPTION) {
    return "Extracting text…";
  }

  if (description === BREAKDOWN_PENDING_JOB_DESCRIPTION) {
    return "Generating breakdown…";
  }

  return `${description}…`;
}

/** Second line under the blocking dialog's title; breakdown generation is the only long-running job that needs it today. */
function pendingJobLoadingDescription(description: string): string | undefined {
  if (description === BREAKDOWN_PENDING_JOB_DESCRIPTION) {
    return "This may take a few minutes, please keep this tab open.";
  }

  return undefined;
}

const NAV_GROUPS: NavGroup[] = [
  {
    id: "top",
    items: [
      { key: ProjectPanelRoute.Dashboard, label: "Dashboard", icon: SpaceDashboardOutlinedIcon, disabled: true },
      { key: ProjectPanelRoute.Script, label: "Screenplay", icon: DescriptionOutlinedIcon },
      { key: ProjectPanelRoute.Breakdown, label: "Breakdown", icon: CalendarViewMonthOutlinedIcon, disabled: true }
    ]
  },
  {
    id: "peoplePlaces",
    items: [
      { key: ProjectPanelRoute.Characters, label: "Characters", icon: FaceIcon },
      { key: ProjectPanelRoute.Locations, label: "Locations", icon: MapsHomeWorkOutlinedIcon },
      { key: ProjectPanelRoute.Props, label: "Props", icon: TapasOutlinedIcon, disabled: true }
    ]
  },
  {
    id: "media",
    items: [
      { key: ProjectPanelRoute.ImageFrame, label: "Image frame", icon: ImageOutlinedIcon },
      { key: ProjectPanelRoute.Video, label: "Video", icon: PlayCircleOutlineOutlinedIcon },
      { key: ProjectPanelRoute.Dialogue, label: "Dialogue", icon: MicIcon, disabled: true },
      { key: ProjectPanelRoute.Audio, label: "Music & SFX", icon: AudiotrackOutlinedIcon, disabled: true },
      { key: ProjectPanelRoute.Timeline, label: "Timeline", icon: LocalMoviesOutlinedIcon, disabled: true }
    ]
  },
  {
    id: "actions",
    items: [{ key: ProjectPanelRoute.Settings, label: "Settings", icon: SettingsOutlinedIcon }]
  }
];

/** Path segment values for `/:projectId/:panel` (`:entityId` optional). */
const VALID_PROJECT_PANEL_KEYS = new Set<string>(Object.values(ProjectPanelRoute));

/** Frame + Video share one storyboard timeline / `activeStoryboardShotIndex`; do not reset when switching between them. */
const STORYBOARD_SHOT_TIMELINE_PANEL_KEYS = new Set<string>([ProjectPanelRoute.ImageFrame, ProjectPanelRoute.Video]);

const ProjectPage = () => {
  const store = useStore();
  const { actionStore } = store;
  const { projectDetails } = actionStore;
  const queryClient = useQueryClient();
  const lastRouteProjectIdRef = useRef<string | null>(null);
  /** After URL→MobX sync in layout, skip one passive store→URL run (it would still see stale `desiredPanelPath` from the same commit). */
  const skipStoreToUrlOnceAfterUrlHydrationRef = useRef(false);
  const [isNavCollapsed, setIsNavCollapsed] = useState(true);
  const [isLeftToolsPanelOpen, setIsLeftToolsPanelOpen] = useState(true);
  const [isRightSidebarOpen, setIsRightSidebarOpen] = useState(false);
  const isLeftToolsPanelOpenRef = useRef(isLeftToolsPanelOpen);
  isLeftToolsPanelOpenRef.current = isLeftToolsPanelOpen;
  const wasLeftToolsPanelOpenBeforePlaybackRef = useRef(true);
  const handleRunTimelinePlaybackChange = useCallback((isActive: boolean) => {
    if (isActive) {
      wasLeftToolsPanelOpenBeforePlaybackRef.current = isLeftToolsPanelOpenRef.current;
      setIsLeftToolsPanelOpen(false);
      setIsRightSidebarOpen(false);
    } else {
      setIsLeftToolsPanelOpen(wasLeftToolsPanelOpenBeforePlaybackRef.current);
    }
  }, []);
  const [isRenameDialogOpen, setIsRenameDialogOpen] = useState(false);
  const { projectId, panel, entityId } = useParams<{ projectId: string; panel: string; entityId?: string }>();
  const location = useLocation();
  const navigate = useNavigate();
  const libraryHighlight = useMemo((): LibraryMediaHighlight | null => parseLibraryMediaHighlightFromSearch(location.search), [location.search]);
  const { data: projectData, isError, isLoading: isProjectLoading, isFetched, error, refetch: refetchProject } = useProjectDetails(projectId ?? null);
  const styleDnaId = assignedStyleDnaIdFromProject(projectData ?? projectDetails ?? null, projectId ?? null);
  const styleQuery = useStyle(projectId ?? null, styleDnaId, { enabled: styleDnaId !== null });
  const updateProjectMutation = useUpdateProject();
  const updateProjectMetaV2Mutation = useUpdateProjectMetaV2<Pick<ProjectDetailsProject, "pending_job" | "breakdown_generated_at">>();
  const patchProjectCache = useProjectCachePatch();
  const projectPermissions = useProjectPermissions(projectData ?? projectDetails ?? null);
  const { canWrite, isOwner, isLocked, ownerEmail } = projectPermissions;
  const lockProjectMutation = useLockProject();
  const unlockProjectMutation = useUnlockProject();

  useEffect(() => {
    if (libraryHighlight !== null) {
      setIsRightSidebarOpen(true);
    }
  }, [libraryHighlight]);

  const handleLibraryHighlightConsumed = useCallback(() => {
    const cleanedSearch = searchWithoutLibraryMediaHighlight(location.search);
    if (cleanedSearch === location.search) {
      return;
    }

    void navigate({ pathname: location.pathname, search: cleanedSearch }, { replace: true });
  }, [location.pathname, location.search, navigate]);

  useEffect(() => {
    if (!projectId || panel === undefined) {
      return;
    }

    if (!VALID_PROJECT_PANEL_KEYS.has(panel)) {
      void navigate(projectPanelPath(projectId, DEFAULT_TAB_REDIRECT_PATH), { replace: true });
      return;
    }

    if (entityId !== undefined && entityId.length > 0 && !projectPanelUsesEntitySlug(panel)) {
      void navigate(projectPanelPath(projectId, panel), { replace: true });
    }
  }, [projectId, panel, entityId, navigate]);

  const detailsForSlugSync = projectData ?? projectDetails;

  useLayoutEffect(() => {
    skipStoreToUrlOnceAfterUrlHydrationRef.current = false;

    if (!detailsForSlugSync?.project || projectId === undefined || panel === undefined) {
      return;
    }

    if (!VALID_PROJECT_PANEL_KEYS.has(panel) || !projectPanelUsesEntitySlug(panel)) {
      return;
    }

    const nextIndex = activeIndexForProjectPanelEntity(panel, detailsForSlugSync, entityId);
    if (nextIndex === null) {
      return;
    }

    let applied = false;

    if (panel === ProjectPanelRoute.Characters && actionStore.activeCharacterIndex !== nextIndex) {
      actionStore.setActiveCharacterIndex(nextIndex);
      applied = true;
    } else if (panel === ProjectPanelRoute.Locations && actionStore.activeLocationIndex !== nextIndex) {
      actionStore.setActiveLocationIndex(nextIndex);
      applied = true;
    } else if ((panel === ProjectPanelRoute.ImageFrame || panel === ProjectPanelRoute.Video) && actionStore.activeStoryboardShotIndex !== nextIndex) {
      actionStore.setActiveStoryboardShotIndex(nextIndex);
      applied = true;
    }

    if (applied && entityId !== undefined && entityId.length > 0 && entityIdExistsInProjectPanel(panel, detailsForSlugSync, entityId)) {
      skipStoreToUrlOnceAfterUrlHydrationRef.current = true;
    }
  }, [actionStore, detailsForSlugSync, entityId, panel, projectId]);

  const desiredEntitySlug = useMemo(() => {
    if (panel === undefined || !projectPanelUsesEntitySlug(panel) || detailsForSlugSync === null || detailsForSlugSync === undefined) {
      return null;
    }

    return (
      entitySlugForProjectPanel(
        panel,
        detailsForSlugSync,
        actionStore.activeStoryboardShotIndex,
        actionStore.activeCharacterIndex,
        actionStore.activeLocationIndex
      ) ?? null
    );
  }, [
    actionStore.activeCharacterIndex,
    actionStore.activeLocationIndex,
    actionStore.activeStoryboardShotIndex,
    detailsForSlugSync,
    panel
  ]);

  const desiredPanelPath = useMemo(() => {
    if (!projectId || panel === undefined || !VALID_PROJECT_PANEL_KEYS.has(panel)) {
      return null;
    }

    if (projectPanelUsesEntitySlug(panel) && (detailsForSlugSync === null || detailsForSlugSync === undefined)) {
      return null;
    }

    return projectPanelPath(projectId, panel, desiredEntitySlug);
  }, [desiredEntitySlug, detailsForSlugSync, panel, projectId]);

  useEffect(() => {
    if (skipStoreToUrlOnceAfterUrlHydrationRef.current) {
      skipStoreToUrlOnceAfterUrlHydrationRef.current = false;
      return;
    }

    if (desiredPanelPath === null) {
      return;
    }

    if (location.pathname === desiredPanelPath) {
      return;
    }

    void navigate(desiredPanelPath, { replace: true });
  }, [desiredPanelPath, location.pathname, navigate, actionStore.activeStoryboardShotIndex]);

  useEffect(() => {
    if (!projectId || desiredPanelPath === null || panel === ProjectPanelRoute.StyleDna) {
      return;
    }

    const now = Date.now();
    saveLastVisitedProjectPanelPath(projectId, desiredPanelPath, now);
    saveLastVisitedProject(projectId, now);

    const entityGroup = projectPanelEntityGroup(panel);
    if (entityGroup !== null && desiredEntitySlug !== null) {
      saveLastVisitedProjectEntityId(projectId, entityGroup, desiredEntitySlug, now);
    }
  }, [desiredEntitySlug, desiredPanelPath, panel, projectId]);

  const { activeKey, leftToolsKey } = useMemo(() => {
    if (panel === undefined || !VALID_PROJECT_PANEL_KEYS.has(panel)) {
      return { activeKey: DEFAULT_TAB_REDIRECT_PATH, leftToolsKey: DEFAULT_TAB_REDIRECT_PATH };
    }

    if (panel === ProjectPanelRoute.StyleDna) {
      return { activeKey: ProjectPanelRoute.StyleDna, leftToolsKey: ProjectPanelRoute.StyleDna };
    }

    return { activeKey: panel, leftToolsKey: panel };
  }, [panel]);

  const { scriptWorkflowTransactionIds } = actionStore;
  const { textExtraction: scriptTextExtractionId, scriptGeneration: scriptGenerationId } = scriptWorkflowTransactionIds;
  const pendingJob = (projectData ?? projectDetails)?.project?.pending_job ?? null;
  const pendingJobTransactionId =
    typeof pendingJob?.transaction_id === "string" && pendingJob.transaction_id.length > 0 ? pendingJob.transaction_id : null;
  const pendingJobDescription =
    typeof pendingJob?.description === "string" && pendingJob.description.length > 0 ? pendingJob.description : "Running background job";

  const { transactionQuery: textExtractionTransactionQuery } = useTransactionPolling(scriptTextExtractionId, {
    intervalMs: DEFAULT_TRANSACTION_INTERVAL_MS
  });
  const { transactionQuery: scriptGenTransactionQuery } = useTransactionPolling(scriptGenerationId, {
    intervalMs: DEFAULT_TRANSACTION_INTERVAL_MS
  });
  const { transactionQuery: pendingJobTransactionQuery } = useTransactionPolling(pendingJobTransactionId, {
    intervalMs: DEFAULT_TRANSACTION_INTERVAL_MS
  });

  const isGlobalPendingJobRunning = pendingJobTransactionId !== null;

  useEffect(() => {
    const status = textExtractionTransactionQuery.data?.status;
    if ((status === "completed" || status === "error") && scriptTextExtractionId) {
      actionStore.clearScriptWorkflowTransactionId("textExtraction");
      void queryClient.invalidateQueries({ queryKey: ["project", projectId] });
    }
  }, [queryClient, projectId, scriptTextExtractionId, textExtractionTransactionQuery.data?.status]);

  useEffect(() => {
    const status = scriptGenTransactionQuery.data?.status;
    if ((status === "completed" || status === "error") && scriptGenerationId) {
      actionStore.clearScriptWorkflowTransactionId("scriptGeneration");
      void queryClient.invalidateQueries({ queryKey: ["project", projectId] });
    }
  }, [queryClient, projectId, scriptGenerationId, scriptGenTransactionQuery.data?.status]);

  const lastHandledPendingJobTxRef = useRef<string | null>(null);
  useEffect(() => {
    const status = pendingJobTransactionQuery.data?.status;
    if (pendingJobTransactionId === null || (status !== "completed" && status !== "error")) {
      return;
    }

    if (lastHandledPendingJobTxRef.current === pendingJobTransactionId) {
      return;
    }

    lastHandledPendingJobTxRef.current = pendingJobTransactionId;

    const clearPendingJob = async () => {
      const refreshed = await refetchProject();
      const details = refreshed.data ?? actionStore.projectDetails ?? projectData ?? null;
      if (!details?.project || !projectId) {
        return;
      }

      const currentTx = details.project.pending_job?.transaction_id;
      if (currentTx !== pendingJobTransactionId) {
        // Another workflow overwrote pending_job; do not clear the newer transaction.
        return;
      }

      const isBreakdownJob = pendingJobDescription === BREAKDOWN_PENDING_JOB_DESCRIPTION;
      const clearedFields =
        status === "completed" && isBreakdownJob
          ? { pending_job: null, breakdown_generated_at: new Date().toISOString() }
          : { pending_job: null };

      const metaVersion = getMetaVersion(details);
      if (metaVersion === undefined) {
        try {
          const result = await updateProjectMutation.mutateAsync({
            projectId,
            body: {
              ...details,
              project: {
                ...details.project,
                ...clearedFields
              }
            }
          });
          actionStore.setProjectDetails(result);
        } catch {
          await refetchProject();
        }
      } else {
        try {
          const savedMeta = await updateProjectMetaV2Mutation.mutateAsync({
            projectId,
            data: clearedFields,
            expectedVersion: metaVersion
          });
          patchProjectCache(projectId, (d) => patchProjectMetaMerged(d, savedMeta.data, savedMeta.version));
        } catch {
          await refetchProject();
        }
      }

      if (status === "error") {
        toastError(`Something went wrong while ${pendingJobDescription.toLowerCase()}`);
      } else if (isBreakdownJob) {
        toastSuccess("Breakdown generation complete");
      }

      void queryClient.invalidateQueries({ queryKey: ["project", projectId] });
    };

    void clearPendingJob();
  }, [
    actionStore,
    pendingJobTransactionId,
    pendingJobDescription,
    pendingJobTransactionQuery.data?.status,
    patchProjectCache,
    projectData,
    projectId,
    queryClient,
    refetchProject,
    updateProjectMetaV2Mutation,
    updateProjectMutation
  ]);

  const prevActiveKeyRef = useRef(activeKey);
  useEffect(() => {
    if (prevActiveKeyRef.current !== activeKey && activeKey === ProjectPanelRoute.Script) {
      void refetchProject();
    }

    prevActiveKeyRef.current = activeKey;
  }, [activeKey, refetchProject]);

  useEffect(() => {
    const nextId = projectId ?? null;
    if (nextId === null || nextId.length === 0) {
      lastRouteProjectIdRef.current = null;
      return;
    }

    if (lastRouteProjectIdRef.current === nextId) {
      return;
    }

    lastRouteProjectIdRef.current = nextId;

    // Route changed to a different project: clear any UI state driven by the old project,
    // and drop/refresh React Query caches so we don't show stale content.
    actionStore.clearProjectDetails();

    // Force fresh reads for project-scoped data.
    void queryClient.invalidateQueries({ queryKey: ["project", nextId] });
    queryClient.removeQueries({ queryKey: ["text"] });
    queryClient.removeQueries({ queryKey: ["transaction"] });
  }, [projectId, actionStore, queryClient]);

  useEffect(() => {
    if (!isFetched || !projectData?.project_id) {
      return;
    }

    const isDifferentProject = projectData.project_id !== projectDetails?.project_id;
    if (isDifferentProject || isProjectDetailsNewerThan(projectData, projectDetails)) {
      actionStore.setProjectDetails(projectData);
    }
  }, [isFetched, projectData, projectDetails?.project_id, projectDetails?.project?.timestamp, actionStore]);

  useEffect(() => {
    if (!isError || !error) {
      return;
    }

    const status = (error as { response?: { status?: number } })?.response?.status;
    if (status && status >= 400) {
      void navigate(AppRoute.Home, { replace: true });
    } else {
      toastError("Error fetching project details");
    }
  }, [isError, error, navigate]);

  const navWidth = isNavCollapsed ? NAV_RAIL_COLLAPSED_WIDTH_PX : NAV_RAIL_EXPANDED_WIDTH_PX;
  const navGroups = useMemo<NavGroup[]>(() => {
    const project = (projectData ?? projectDetails)?.project;
    const characterCount = project ? (project.characters?.length ?? 0) : undefined;
    const locationCount = project ? (project.locations?.length ?? 0) : undefined;
    const storyboardShots = project?.["storyboard-shots"];
    let storyboardImageFrameCount: number | undefined;
    let storyboardVideoCount: number | undefined;

    if (storyboardShots !== undefined) {
      storyboardImageFrameCount = 0;
      storyboardVideoCount = 0;

      for (const shot of storyboardShots) {
        const approvedImageId = shot["approved-timeline-images"]?.[0];
        const approvedTimelineImageId = typeof approvedImageId === "string" ? approvedImageId.trim() : "";
        if (approvedTimelineImageId.length > 0) {
          storyboardImageFrameCount += 1;
        }

        if (approvedStoryboardShotVideoId(shot) !== null) {
          storyboardVideoCount += 1;
        }
      }
    }

    return NAV_GROUPS.map((group) => ({
      ...group,
      items: group.items.map((item) => {
        if (item.key === ProjectPanelRoute.Characters && characterCount !== undefined) {
          return { ...item, count: characterCount };
        }

        if (item.key === ProjectPanelRoute.Locations && locationCount !== undefined) {
          return { ...item, count: locationCount };
        }

        if (item.key === ProjectPanelRoute.ImageFrame && storyboardImageFrameCount !== undefined) {
          return { ...item, count: storyboardImageFrameCount };
        }

        if (item.key === ProjectPanelRoute.Video && storyboardVideoCount !== undefined) {
          return { ...item, count: storyboardVideoCount };
        }

        if (item.key === ProjectPanelRoute.Settings) {
          return { ...item, disabled: !canWrite };
        }

        return item;
      })
    }));
  }, [projectData, projectDetails, canWrite]);

  const activeLabel = navGroups.flatMap((g) => g.items).find((x) => x.key === activeKey)?.label ?? "Home";

  function resolveLeftToolsLabel(): string {
    return navGroups.flatMap((g) => g.items).find((x) => x.key === leftToolsKey)?.label ?? activeLabel;
  }

  const leftToolsLabel = resolveLeftToolsLabel();

  const centerWorkspace = useMemo((): ReactNode => {
    if (activeKey === ProjectPanelRoute.Script) {
      return <ScriptMainSpace />;
    }

    if (activeKey === ProjectPanelRoute.StyleDna) {
      return <StyleDnaMainSpace />;
    }

    if (activeKey === ProjectPanelRoute.Settings) {
      return <SystemSettingsMainSpace />;
    }

    return (
      <PageContent
        tabKey={activeKey}
        onToggleSidebar={() => setIsRightSidebarOpen((v) => !v)}
        onRunTimelinePlaybackChange={handleRunTimelinePlaybackChange}
        {...(activeKey === ProjectPanelRoute.ImageFrame || activeKey === ProjectPanelRoute.Video
          ? { uploadTarget: "storyboard-shots" as const }
          : {})}
        {...(activeKey === ProjectPanelRoute.Characters && { uploadTarget: "characters" as const })}
        {...(activeKey === ProjectPanelRoute.Locations && { uploadTarget: "locations" as const })}
      />
    );
  }, [activeKey]);

  const hideRightSidebar =
    activeKey === ProjectPanelRoute.Script || activeKey === ProjectPanelRoute.StyleDna || activeKey === ProjectPanelRoute.Settings;

  const handleSelectLeftNav = useCallback(
    (key: string) => {
      if (isGlobalPendingJobRunning) {
        return;
      }

      if (!projectId) {
        return;
      }

      // Re-opens the tools panel whenever the user interacts with left nav, even if it was
      // closed via its own X button or the user re-clicks the already-active item.
      setIsLeftToolsPanelOpen(true);

      const fromPanel = panel;
      const fromUsesStoryboardShotTimeline =
        fromPanel !== undefined && VALID_PROJECT_PANEL_KEYS.has(fromPanel) && STORYBOARD_SHOT_TIMELINE_PANEL_KEYS.has(fromPanel);
      const toUsesStoryboardShotTimeline = STORYBOARD_SHOT_TIMELINE_PANEL_KEYS.has(key);
      const preserveStoryboardShotIndex = fromUsesStoryboardShotTimeline && toUsesStoryboardShotTimeline;

      // Only restore indices for the panel we are navigating *to*. Changing storyboard shot index
      // while the URL still points at imageFrame/video makes `desiredPanelPath` jump shots
      // and the passive store→URL effect wins the race over `navigate` below.
      const details = projectData ?? projectDetails;
      const entityGroup = projectPanelEntityGroup(key);
      const savedEntityId = entityGroup === null ? null : readLastVisitedProjectEntityId(projectId, entityGroup);
      const restoredIndex = activeIndexForProjectPanelEntity(key, details ?? null, savedEntityId ?? undefined) ?? 0;

      const nextCharacterIndex = key === ProjectPanelRoute.Characters ? restoredIndex : actionStore.activeCharacterIndex;
      const nextLocationIndex = key === ProjectPanelRoute.Locations ? restoredIndex : actionStore.activeLocationIndex;
      const nextStoryboardShotIndex =
        toUsesStoryboardShotTimeline && !preserveStoryboardShotIndex ? restoredIndex : actionStore.activeStoryboardShotIndex;

      if (key === ProjectPanelRoute.Characters) {
        actionStore.setActiveCharacterIndex(nextCharacterIndex);
      }

      if (key === ProjectPanelRoute.Locations) {
        actionStore.setActiveLocationIndex(nextLocationIndex);
      }

      if (toUsesStoryboardShotTimeline && !preserveStoryboardShotIndex) {
        actionStore.setActiveStoryboardShotIndex(nextStoryboardShotIndex);
      }

      const slug =
        projectPanelUsesEntitySlug(key) && details !== null && details !== undefined
          ? entitySlugForProjectPanel(key, details, nextStoryboardShotIndex, nextCharacterIndex, nextLocationIndex)
          : undefined;

      void navigate(projectPanelPath(projectId, key, slug));
    },
    [isGlobalPendingJobRunning, projectId, panel, navigate, actionStore, projectData, projectDetails]
  );

  const handleRenameProject = useCallback(
    async (nextName: string) => {
      try {
        if (!projectData?.project) {
          return;
        }

        if (!canWrite) {
          toastError("This project is locked by its owner. Only the owner can make changes while it is locked.", { position: "bottom-left" });
          return;
        }

        const result = await updateProjectMutation.mutateAsync({
          projectId: projectData.project_id,
          body: {
            ...projectData,
            project_name: nextName,
            project: {
              ...projectData.project,
              "project-name": nextName
            } as ProjectDetailsProject
          }
        });
        actionStore.setProjectDetails(result);
        setIsRenameDialogOpen(false);
      } catch (renameError) {
        const message = renameError instanceof Error ? renameError.message : "Failed to rename project";
        toastError(message);
      }
    },
    [projectData, canWrite]
  );

  const handleToggleLock = useCallback(async () => {
    if (!projectId || !isOwner) {
      return;
    }

    try {
      if (isLocked) {
        await unlockProjectMutation.mutateAsync(projectId);
        toastSuccess("Project unlocked - everyone can edit again.", { position: "bottom-left" });
      } else {
        await lockProjectMutation.mutateAsync({ projectId, ownerEmail: ownerEmail ?? "" });
        toastSuccess("Project locked - only you can edit while it's locked.", { position: "bottom-left" });
      }
    } catch (toggleLockError) {
      const message = toggleLockError instanceof Error ? toggleLockError.message : "Failed to update the project lock.";
      toastError(message, { position: "bottom-left" });
    }
  }, [projectId, isOwner, isLocked, ownerEmail, lockProjectMutation, unlockProjectMutation]);

  const hasStyleDnaApplied = styleDnaGenerationsHaveSelected(styleQuery.data?.style_dna_generations);

  return (
    <Box sx={{ width: "100vw", height: "100vh", display: "flex", flexDirection: "column" }}>
      <AppHeader
        height={APP_HEADER_HEIGHT_PX}
        variant="project"
        projectId={projectId}
        projectName={projectData?.project_name ?? ""}
        tokensUsed={projectData?.tokens_used ?? 0}
        interactionDisabled={isGlobalPendingJobRunning}
        hasStyleDnaApplied={hasStyleDnaApplied}
        readOnly={!canWrite}
        isProjectDetailsLoaded={Boolean(projectData ?? projectDetails)}
        isOwner={isOwner}
        isLocked={isLocked}
        ownerEmail={ownerEmail}
        onToggleLock={() => void handleToggleLock()}
        isLockActionPending={lockProjectMutation.isPending || unlockProjectMutation.isPending}
        onProjectAction={(value) => {
          if (isGlobalPendingJobRunning) {
            return;
          }

          if (value === "rename") {
            if (!canWrite) {
              toastError("This project is locked by its owner. Only the owner can make changes while it is locked.", { position: "bottom-left" });
              return;
            }

            setIsRenameDialogOpen(true);
            return;
          }

          if (value === "settings") {
            if (!canWrite) {
              toastError("This project is locked by its owner. Only the owner can make changes while it is locked.", { position: "bottom-left" });
              return;
            }

            handleSelectLeftNav(ProjectPanelRoute.Settings);
            return;
          }

          toastInfo(`TODO: Project action - ${value}`);
        }}
        onClickSetStyleDna={() => {
          if (isGlobalPendingJobRunning) {
            return;
          }

          if (!projectId) {
            return;
          }

          void navigate(projectPanelPath(projectId, ProjectPanelRoute.StyleDna));
        }}
        onClickShare={() => {
          if (isGlobalPendingJobRunning) {
            return;
          }

          toastInfo("TODO: Share");
        }}
      />
      <Box sx={{ flex: 1, minHeight: 0, display: "flex" }}>
        <LoadingDialog open={isProjectLoading || !projectData?.project_id} modal />
        <LoadingDialog
          open={isGlobalPendingJobRunning}
          label={pendingJobLoadingLabel(pendingJobDescription)}
          description={pendingJobLoadingDescription(pendingJobDescription)}
          modal
        />
        <LeftSidebar
          headerHeight={APP_HEADER_HEIGHT_PX}
          width={navWidth}
          isCollapsed={isNavCollapsed}
          onToggleCollapsed={() => setIsNavCollapsed((v) => !v)}
          navGroups={navGroups}
          activeKey={activeKey}
          disableSelection={isGlobalPendingJobRunning}
          onSelect={handleSelectLeftNav}
        />
        <LeftToolsPanel
          headerHeight={APP_HEADER_HEIGHT_PX}
          width={leftToolsKey === ProjectPanelRoute.StyleDna ? LEFT_TOOLS_STYLE_DNA_PANEL_WIDTH : LEFT_TOOLS_PANEL_WIDTH_PX}
          activeKey={leftToolsKey}
          activeLabel={leftToolsLabel}
          // The old "Story to Screenplay" left panel is gone (superseded by the Project settings modal +
          // starting-point picker inside ScriptMainSpace itself) - never open the tools panel for this route.
          open={isLeftToolsPanelOpen && leftToolsKey !== ProjectPanelRoute.Script}
          onClose={() => setIsLeftToolsPanelOpen(false)}
        />
        <Suspense
          fallback={
            <Box sx={{ flex: 1, minHeight: 0 }}>
              <Loading fullScreen={false} />
            </Box>
          }
        >
          {centerWorkspace}
        </Suspense>
        {hideRightSidebar ? null : (
          <RightSidebar
            headerHeight={APP_HEADER_HEIGHT_PX}
            width={RIGHT_SIDEBAR_WIDTH_PX}
            open={isRightSidebarOpen}
            onClose={() => setIsRightSidebarOpen(false)}
            libraryHighlight={libraryHighlight}
            onLibraryHighlightConsumed={handleLibraryHighlightConsumed}
          />
        )}
      </Box>
      <RenameProjectDialog
        open={isRenameDialogOpen}
        initialName={projectData?.project_name ?? ""}
        isSaving={updateProjectMutation.isPending}
        onClose={() => setIsRenameDialogOpen(false)}
        onSave={handleRenameProject}
      />
    </Box>
  );
};

export default observer(ProjectPage);
